import React from 'react';
import { useFormContext } from 'react-hook-form';
import { BedIcon, BedDoubleIcon, UsersIcon, HomeIcon } from 'lucide-react';
import RoomCountSelect from './RoomCountSelect';
import TotalDisplay from './TotalDisplay';
import { calculateTotals, ensureNumber } from './utils';

interface RoomsSectionProps {
  readOnly?: boolean;
  isCabin?: boolean;
}

const RoomsSection = ({ readOnly = false, isCabin = false }: RoomsSectionProps) => {
  const { watch, setValue } = useFormContext();
  const basePath = 'repat';

  // Obtener valores según el tipo de sección
  let single, double, triple, quadruple, quintuple, sextuple, extraBeds;
  
  if (isCabin) {
    // Para cabañas, solo observar campos de cabañas
    single = 0; // Las cabañas no tienen singles
    double = ensureNumber(watch(`${basePath}.cabinsDouble`, 0));
    triple = ensureNumber(watch(`${basePath}.cabinsTriple`, 0));
    quadruple = ensureNumber(watch(`${basePath}.cabinsQuadruple`, 0));
    quintuple = ensureNumber(watch(`${basePath}.cabinsQuintuple`, 0));
    sextuple = ensureNumber(watch(`${basePath}.cabinsSextuple`, 0));
    extraBeds = ensureNumber(watch(`${basePath}.cabinsExtraBeds`, 0));
  } else {
    // Para habitaciones, observar campos de habitaciones
    single = ensureNumber(watch(`${basePath}.roomsSingle`, 0));
    double = ensureNumber(watch(`${basePath}.roomsDouble`, 0));
    triple = ensureNumber(watch(`${basePath}.roomsTriple`, 0));
    quadruple = ensureNumber(watch(`${basePath}.roomsQuadruple`, 0));
    quintuple = ensureNumber(watch(`${basePath}.roomsQuintuple`, 0));
    sextuple = ensureNumber(watch(`${basePath}.roomsSextuple`, 0));
    extraBeds = ensureNumber(watch(`${basePath}.roomsExtraBeds`, 0));
  }

  // Obtener valores de apartamentos
  const apartmentsSingle = ensureNumber(watch(`${basePath}.apartmentsSingle`, 0));
  const apartmentsDouble = ensureNumber(watch(`${basePath}.apartmentsDouble`, 0));
  const apartmentsTriple = ensureNumber(watch(`${basePath}.apartmentsTriple`, 0));
  const apartmentsQuadruple = ensureNumber(watch(`${basePath}.apartmentsQuadruple`, 0));
  const apartmentsQuintuple = ensureNumber(watch(`${basePath}.apartmentsQuintuple`, 0));
  const apartmentsSextuple = ensureNumber(watch(`${basePath}.apartmentsSextuple`, 0));

  // Calcular totales de habitaciones
  const { totalUnits: totalRooms, totalPlaces: totalRoomPlaces } = calculateTotals({
    single,
    double,
    triple,
    quadruple,
    quintuple,
    sextuple,
    extraBeds
  });

  // Calcular totales de apartamentos
  const { totalUnits: totalApartments, totalPlaces: totalApartmentPlaces } = calculateTotals({
    single: apartmentsSingle,
    double: apartmentsDouble,
    triple: apartmentsTriple,
    quadruple: apartmentsQuadruple,
    quintuple: apartmentsQuintuple,
    sextuple: apartmentsSextuple,
    extraBeds: 0
  });

  // Actualizar totales en el formulario
  React.useEffect(() => {
    // Solo actualizar totales de la sección actual
    if (isCabin) {
      // Para cabañas, solo actualizar totales de cabañas
      setValue(`${basePath}.totalCabins`, totalRooms);
      setValue(`${basePath}.totalCabinPlaces`, totalRoomPlaces);
    } else {
      // Para habitaciones, actualizar totales de habitaciones y apartamentos
      setValue(`${basePath}.totalRooms`, totalRooms);
      setValue(`${basePath}.totalRoomPlaces`, totalRoomPlaces);
      setValue(`${basePath}.totalApartments`, totalApartments);
      setValue(`${basePath}.totalApartmentPlaces`, totalApartmentPlaces);
    }
  }, [totalRooms, totalRoomPlaces, totalApartments, totalApartmentPlaces, setValue, basePath, isCabin]);

  return (
    <div className="space-y-8">
      {/* Sección de Habitaciones */}
      <div className="space-y-4">
        <h2 className="text-lg font-medium">{isCabin ? 'Cabañas' : 'Habitaciones'}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          
          {!isCabin && (
            <RoomCountSelect 
              icon={BedIcon}
              label="Singles"
              fieldName={`${basePath}.${'roomsSingle'}`}
              defaultValue="0"
              readOnly={readOnly}
            />
          )}
          <RoomCountSelect 
            icon={BedDoubleIcon}
            label="Dobles"
            fieldName={`${basePath}.${isCabin ? 'cabinsDouble' : 'roomsDouble'}`}
            defaultValue="0"
            readOnly={readOnly}
          />
          <RoomCountSelect 
            icon={UsersIcon}
            label="Triples"
            fieldName={`${basePath}.${isCabin ? 'cabinsTriple' : 'roomsTriple'}`}
            defaultValue="0"
            readOnly={readOnly}
          />
          <RoomCountSelect 
            icon={UsersIcon}
            label="Cuádruples"
            fieldName={`${basePath}.${isCabin ? 'cabinsQuadruple' : 'roomsQuadruple'}`}
            defaultValue="0"
            readOnly={readOnly}
          />
          <RoomCountSelect 
            icon={UsersIcon}
            label="Quíntuples"
            fieldName={`${basePath}.${isCabin ? 'cabinsQuintuple' : 'roomsQuintuple'}`}
            defaultValue="0"
            readOnly={readOnly}
          />
          <RoomCountSelect 
            icon={UsersIcon}
            label="Séxtuples"
            fieldName={`${basePath}.${isCabin ? 'cabinsSextuple' : 'roomsSextuple'}`}
            defaultValue="0"
            readOnly={readOnly}
          />
          
          <RoomCountSelect 
            icon={BedIcon}
            label="Camas adicionales"
            fieldName={`${basePath}.${isCabin ? 'cabinsExtraBeds' : 'roomsExtraBeds'}`}
            defaultValue="0"
            readOnly={readOnly}
          />
        </div>
        
        {/* Totales de Habitaciones */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <TotalDisplay 
            label={`Total ${isCabin ? 'Cabañas' : 'Habitaciones'}`}
            fieldName={`${basePath}.total${isCabin ? 'Cabins' : 'Rooms'}`}
            value={String(totalRooms)}
            readOnly={readOnly}
            icon={isCabin ? HomeIcon : BedIcon}
          />
          <TotalDisplay 
            label="Total Plazas"
            fieldName={`${basePath}.total${isCabin ? 'Cabin' : 'Room'}Places`}
            value={String(totalRoomPlaces)}
            readOnly={readOnly}
            icon={UsersIcon}
          />
        </div>
      </div>

      {/* Sección de Apartamentos */}
      {!isCabin && (
        <div className="space-y-4">
          <h2 className="text-lg font-medium">Apartamentos</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <RoomCountSelect 
              icon={HomeIcon}
              label="Singles"
              fieldName={`${basePath}.apartmentsSingle`}
              defaultValue="0"
              readOnly={readOnly}
            />
            <RoomCountSelect 
              icon={HomeIcon}
              label="Dobles"
              fieldName={`${basePath}.apartmentsDouble`}
              defaultValue="0"
              readOnly={readOnly}
            />
            <RoomCountSelect 
              icon={HomeIcon}
              label="Triples"
              fieldName={`${basePath}.apartmentsTriple`}
              defaultValue="0"
              readOnly={readOnly}
            />
            <RoomCountSelect 
              icon={HomeIcon}
              label="Cuádruples"
              fieldName={`${basePath}.apartmentsQuadruple`}
              defaultValue="0"
              readOnly={readOnly}
            />
            <RoomCountSelect 
              icon={HomeIcon}
              label="Quíntuples"
              fieldName={`${basePath}.apartmentsQuintuple`}
              defaultValue="0"
              readOnly={readOnly}
            />
            <RoomCountSelect 
              icon={HomeIcon}
              label="Séxtuples"
              fieldName={`${basePath}.apartmentsSextuple`}
              defaultValue="0"
              readOnly={readOnly}
            />
          </div>
          
          {/* Totales de Apartamentos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <TotalDisplay 
              label="Total Apartamentos"
              fieldName={`${basePath}.totalApartments`}
              value={String(totalApartments)}
              readOnly={readOnly}
              icon={HomeIcon}
            />
            <TotalDisplay 
              label="Total Plazas"
              fieldName={`${basePath}.totalApartmentPlaces`}
              value={String(totalApartmentPlaces)}
              readOnly={readOnly}
              icon={UsersIcon}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomsSection;